/**
 * CreateEventForm
 *
 * Coordinator/Manager-facing form for creating Hub Meeting or Training/
 * Workshop calendar events. Pairs the calendar_events row with an
 * optional linked form_instance carrying topic / additional context
 * (form_instances.calendar_event_id — shipped in Phase 1).
 *
 * Single-page layout (not a multi-step wizard): all four sections — type,
 * event details, topic & notes, attendees — visible at once so the user
 * can scan and edit freely. Submit creates both the event and (if a
 * detail template is configured for the type) the linked form_instance
 * in sequence.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, AlertTriangle, CheckCircle2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { authFetchJson } from '@/lib/authFetch';
import { calendarAPI, EventType, LocationType } from '@/services/api/calendar';
import { formsAPI, FormTemplate } from '@/services/api/forms';

interface CreateEventFormProps {
  /** Optional fixed type — when set, the type picker is hidden. */
  initialType?: 'network_hub_meeting' | 'training_workshop';
  onCreated?: (eventId: number) => void;
  onCancel?: () => void;
}

interface StaffOption {
  user_id: number;
  name: string;
  email?: string;
  role?: string;
}

interface FormState {
  event_type: 'network_hub_meeting' | 'training_workshop';
  // Hub-only sub-type (ILRC / Regional Network Hub / Other)
  hub_event_type: string;
  // Training-only sub-type (Training / Workshop)
  training_type: string;
  title: string;
  date: string; // YYYY-MM-DD
  start_time: string; // HH:MM
  duration_minutes: number;
  location_type: LocationType;
  location_address: string;
  topic: string;
  content_topic_area: string;
  notes: string;
  staff_attendee_ids: number[];
}

const DURATION_OPTIONS = [15, 30, 45, 60, 75, 90, 105, 120, 150, 180, 240];

const TEMPLATE_NAME_BY_TYPE: Record<string, string> = {
  network_hub_meeting: 'Somos — Hub Meeting Detail',
  training_workshop: 'Somos — Training/Workshop Detail',
};

export const CreateEventForm: React.FC<CreateEventFormProps> = ({
  initialType,
  onCreated,
  onCancel,
}) => {
  const { t } = useTranslation();

  const formatDuration = (m: number): string => {
    if (m < 60) return t('events.createEvent.duration_minutes', { count: m });
    const h = Math.floor(m / 60);
    const min = m % 60;
    if (min === 0) return h === 1 ? t('events.createEvent.duration_one_hour') : t('events.createEvent.duration_hours', { count: h });
    return h === 1 ? t('events.createEvent.duration_one_hour_min', { min }) : t('events.createEvent.duration_hours_min', { hours: h, min });
  };
  const [form, setForm] = useState<FormState>({
    event_type: initialType ?? 'network_hub_meeting',
    hub_event_type: '',
    training_type: '',
    title: '',
    date: '',
    start_time: '',
    duration_minutes: 60,
    location_type: 'in_person',
    location_address: '',
    topic: '',
    content_topic_area: '',
    notes: '',
    staff_attendee_ids: [],
  });

  const [staff, setStaff] = useState<StaffOption[] | null>(null);
  const [staffError, setStaffError] = useState<string | null>(null);
  const [detailTemplates, setDetailTemplates] = useState<FormTemplate[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [createdEventId, setCreatedEventId] = useState<number | null>(null);

  // Load active staff for the attendee picker. The endpoint returns rows
  // shaped roughly { user_id, name, email, role, is_active }; we pluck the
  // fields we need.
  //
  // Filter out Trust internal admin roles (super_admin, ops_admin,
  // org_admin) — these accounts have user_organizations rows on most
  // CBOs for support access, but they shouldn't appear in an attendee
  // picker as program staff. CBOs see only their own program-level roles
  // (program_manager, supervisor, staff, plus any org-custom roles like
  // Somos' coordinator/promotora).
  useEffect(() => {
    let cancelled = false;
    const cboId = Number(localStorage.getItem('active_cbo_id') || 0);
    if (!cboId) {
      setStaffError(t('events.createEvent.error_no_org'));
      return;
    }
    const TRUST_ADMIN_ROLES = new Set(['super_admin', 'ops_admin', 'org_admin']);
    authFetchJson<any[]>(`/api/organization-setup/${cboId}/staff`)
      .then((rows) => {
        if (cancelled) return;
        setStaff(
          (rows || [])
            .filter((r) => r.is_active !== false)
            .filter((r) => !TRUST_ADMIN_ROLES.has((r.role || '').toLowerCase()))
            .map((r) => ({
              user_id: r.user_id ?? r.id,
              name: r.name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email,
              email: r.email,
              role: r.role || r.role_display_name,
            }))
            .filter((s) => s.user_id),
        );
      })
      .catch((e: any) => {
        if (cancelled) return;
        setStaffError(e?.message || t('events.createEvent.error_load_staff'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the two event-detail templates so we can link a form_instance to
  // the new event. If a template is missing (admin removed it), we still
  // create the event without the linked form_instance.
  useEffect(() => {
    let cancelled = false;
    formsAPI
      .listFormTemplates({ form_type: 'event_detail' as any, is_active: true })
      .then((templates) => {
        if (!cancelled) setDetailTemplates(templates);
      })
      .catch(() => {
        if (!cancelled) setDetailTemplates([]);
      });
  }, []);

  const detailTemplate = useMemo(() => {
    const expected = TEMPLATE_NAME_BY_TYPE[form.event_type];
    return detailTemplates?.find((t) => t.name === expected) || null;
  }, [detailTemplates, form.event_type]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((s) => ({ ...s, [k]: v }));

  const toggleAttendee = (uid: number) =>
    setForm((s) => ({
      ...s,
      staff_attendee_ids: s.staff_attendee_ids.includes(uid)
        ? s.staff_attendee_ids.filter((x) => x !== uid)
        : [...s.staff_attendee_ids, uid],
    }));

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setServerError(null);
    setCreatedEventId(null);

    if (!form.title.trim()) return setServerError(t('events.createEvent.error_title_required'));
    if (!form.date) return setServerError(t('events.createEvent.error_date_required'));
    if (!form.start_time) return setServerError(t('events.createEvent.error_start_time_required'));

    const start = new Date(`${form.date}T${form.start_time}:00`);
    if (isNaN(start.getTime())) return setServerError(t('events.createEvent.error_invalid_datetime'));
    const end = new Date(start.getTime() + form.duration_minutes * 60_000);

    const subtype =
      form.event_type === 'network_hub_meeting'
        ? form.hub_event_type || null
        : form.training_type || null;

    setSubmitting(true);
    try {
      const event = await calendarAPI.createEvent({
        title: form.title.trim(),
        description: form.topic.trim() || undefined,
        event_type: form.event_type as EventType,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        location_type: form.location_type,
        location_address: form.location_address.trim() || undefined,
        custom_fields: subtype ? { activity_subtype: subtype } : undefined,
        attendees: form.staff_attendee_ids.map((uid) => ({
          user_id: uid,
          attendee_role: 'attendee',
          is_optional: false,
        })),
      } as any);

      // Optional: create a linked form_instance with the topic / sub-type
      // captured in field_data so the detail template renders correctly
      // when someone views the event later.
      if (detailTemplate) {
        const fieldData = buildDetailFieldData(form);
        try {
          await formsAPI.createFormInstance({
            form_template_id: detailTemplate.id,
            calendar_event_id: event.id,
          });
          // The create endpoint doesn't accept field_data on create — it
          // pre-populates from the enrollment. To persist the entered data
          // we'd need to PUT to /api/forms/instances/{id}/submit. Skip for
          // V1 — the calendar event description carries the topic; the
          // form_instance is created as a draft so admins can fill it in
          // later via FormInstanceEditor. (The submit endpoint requires
          // additional plumbing in this wizard; tracked for a follow-up.)
          void fieldData; // silence unused while the submit step is pending
        } catch (e) {
          // Don't fail the whole flow if the linked instance can't be
          // created — the event was the primary artifact.
          console.warn('Linked detail form_instance creation skipped:', e);
        }
      }

      setCreatedEventId(event.id);
      onCreated?.(event.id);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || t('events.createEvent.error_create_failed');
      setServerError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const isHub = form.event_type === 'network_hub_meeting';

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto p-4 space-y-4 pb-12">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t('events.createEvent.title')}</h1>
        <p className="text-sm text-gray-500">
          {t('events.createEvent.subtitle')}
        </p>
      </header>

      {createdEventId && (
        <div className="flex gap-2 items-start text-sm bg-green-50 border border-green-200 text-green-900 rounded-md p-3">
          <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600" />
          <span>{t('events.createEvent.event_created', { id: createdEventId, count: form.staff_attendee_ids.length })}</span>
        </div>
      )}
      {serverError && (
        <div className="flex gap-2 items-start text-sm bg-red-50 border border-red-200 text-red-900 rounded-md p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-red-600" />
          <span>{serverError}</span>
        </div>
      )}

      {/* Section: Type */}
      {!initialType && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t('events.createEvent.section_type')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                checked={isHub}
                onChange={() => set('event_type', 'network_hub_meeting')}
                className="mt-1"
              />
              <span>
                <span className="font-medium">{t('events.createEvent.network_hub_meeting_label')}</span>
                <span className="block text-xs text-gray-500">
                  {t('events.createEvent.network_hub_meeting_desc')}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                checked={!isHub}
                onChange={() => set('event_type', 'training_workshop')}
                className="mt-1"
              />
              <span>
                <span className="font-medium">{t('events.createEvent.training_workshop_label')}</span>
                <span className="block text-xs text-gray-500">
                  {t('events.createEvent.training_workshop_desc')}
                </span>
              </span>
            </label>
          </CardContent>
        </Card>
      )}

      {/* Section: Event details -- TRUS-190: all strings translated */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t('events.createEvent.section_details')}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>{t('events.createEvent.label_title')}<span className="text-red-600 ml-1">*</span></Label>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} required />
          </div>
          <div>
            <Label>{t('events.createEvent.label_date')}<span className="text-red-600 ml-1">*</span></Label>
            <Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required />
          </div>
          <div>
            <Label>{t('events.createEvent.label_start_time')}<span className="text-red-600 ml-1">*</span></Label>
            <Input type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} required />
          </div>
          <div>
            <Label>{t('events.createEvent.label_duration')}</Label>
            <select
              value={form.duration_minutes}
              onChange={(e) => set('duration_minutes', Number(e.target.value))}
              className="block w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-3 py-2 text-sm"
            >
              {DURATION_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {formatDuration(m)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>{t('events.createEvent.label_format')}</Label>
            <div className="flex gap-3 mt-1">
              {(['in_person', 'virtual'] as LocationType[]).map((lt) => (
                <label key={lt} className="inline-flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    checked={form.location_type === lt}
                    onChange={() => set('location_type', lt)}
                  />
                  {lt === 'in_person' ? t('events.createEvent.format_in_person') : t('events.createEvent.format_virtual')}
                </label>
              ))}
            </div>
          </div>
          {form.location_type === 'in_person' && (
            <div className="md:col-span-2">
              <Label>{t('events.createEvent.label_location')}</Label>
              <Input
                placeholder={t('events.createEvent.placeholder_location')}
                value={form.location_address}
                onChange={(e) => set('location_address', e.target.value)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section: Topic & sub-type */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{isHub ? t('events.createEvent.section_hub_details') : t('events.createEvent.section_training_details')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isHub ? (
            <div>
              <Label>{t('events.createEvent.label_event_subtype')}</Label>
              <select
                value={form.hub_event_type}
                onChange={(e) => set('hub_event_type', e.target.value)}
                className="block w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-3 py-2 text-sm"
              >
                <option value="">{t('events.createEvent.select_placeholder')}</option>
                <option value="ILRC">{t('events.createEvent.hub_subtype_ilrc')}</option>
                <option value="Regional Network Hub">{t('events.createEvent.hub_subtype_regional')}</option>
                <option value="Other">{t('events.createEvent.hub_subtype_other')}</option>
              </select>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>{t('events.createEvent.label_training_type')}</Label>
                <select
                  value={form.training_type}
                  onChange={(e) => set('training_type', e.target.value)}
                  className="block w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">{t('events.createEvent.select_placeholder')}</option>
                  <option value="Training">{t('events.createEvent.training_type_training')}</option>
                  <option value="Workshop">{t('events.createEvent.training_type_workshop')}</option>
                </select>
              </div>
              <div>
                <Label>{t('events.createEvent.label_content_topic_area')}</Label>
                <Input
                  value={form.content_topic_area}
                  onChange={(e) => set('content_topic_area', e.target.value)}
                  placeholder={t('events.createEvent.placeholder_content_topic')}
                />
              </div>
            </div>
          )}
          <div>
            <Label>{t('events.createEvent.label_topic_agenda')}</Label>
            <Textarea
              rows={3}
              value={form.topic}
              onChange={(e) => set('topic', e.target.value)}
              placeholder={isHub ? t('events.createEvent.placeholder_hub_topic') : t('events.createEvent.placeholder_training_topic')}
            />
          </div>
          <div>
            <Label>{t('events.createEvent.label_notes')}</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </div>
        </CardContent>
      </Card>

      {/* Section: Attendees */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4" />
            {t('events.createEvent.section_attendance')} ({form.staff_attendee_ids.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {staffError && <div className="text-sm text-red-700">{staffError}</div>}
          {!staff && !staffError && <div className="text-sm text-gray-500">{t('events.createEvent.loading_staff')}</div>}
          {staff && staff.length === 0 && (
            <div className="text-sm text-gray-500">{t('events.createEvent.no_staff')}</div>
          )}
          {staff && staff.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-64 overflow-auto border border-gray-100 dark:border-gray-700 rounded-md p-2">
              {staff.map((s) => (
                <label key={s.user_id} className="flex items-center gap-2 text-sm py-1 px-1 hover:bg-gray-50 dark:hover:bg-gray-800 rounded">
                  <input
                    type="checkbox"
                    checked={form.staff_attendee_ids.includes(s.user_id)}
                    onChange={() => toggleAttendee(s.user_id)}
                  />
                  <span className="flex-1 min-w-0 truncate">
                    {s.name}
                    {s.role && <span className="text-xs text-gray-500 ml-1">· {s.role}</span>}
                  </span>
                </label>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <footer className="flex justify-end gap-2 sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 py-3 -mx-4 px-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            {t('events.createEvent.btn_cancel')}
          </Button>
        )}
        <Button type="submit" disabled={submitting}>
          <Save className="w-4 h-4 mr-2" />
          {submitting ? t('events.createEvent.btn_saving') : t('events.createEvent.btn_create')}
        </Button>
      </footer>
    </form>
  );
};

function buildDetailFieldData(form: FormState): Record<string, any> {
  // Mirrors the seeded com_fields names so the form_instance.field_data
  // aligns with the detail template's field_ids (see
  // app/scripts/seeds/seed_somos_field_config.py).
  if (form.event_type === 'network_hub_meeting') {
    return {
      hub_meeting_date: form.date,
      hub_meeting_name: form.title,
      hub_event_type: form.hub_event_type || null,
      hub_topic: form.topic,
      hub_meeting_format: form.location_type === 'virtual' ? 'Virtual' : 'In-Person',
      hub_duration_minutes: form.duration_minutes,
      hub_staff_attendees: form.staff_attendee_ids,
      hub_notes: form.notes,
    };
  }
  return {
    training_event_date: form.date,
    training_event_name: form.title,
    training_type: form.training_type || null,
    training_content_topic_area: form.content_topic_area,
    training_format: form.location_type === 'virtual' ? 'Virtual' : 'In-Person',
    training_duration_minutes: form.duration_minutes,
    training_attendees: form.staff_attendee_ids,
    training_notes: form.notes,
  };
}

export default CreateEventForm;
